import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import type { SavedCustomReport, ReportPeriod } from "../types";
import { CUSTOM_REPORTS_STORAGE_KEY } from "../constants";
import CustomDropdown from "../../../../../components/CustomDropdown/CustomDropdown";

const GROUPS_KEY = "novala.customReportGroups";
const ADD_NEW = "__new__";

interface Props {
  open: boolean;
  onClose: () => void;
  accountId: string;
  accountName: string;
  period: ReportPeriod;
  customFrom?: string;
  customTo?: string;
  onSaved?: (report: SavedCustomReport) => void;
}

export default function SaveReportModal({ open, onClose, accountId, accountName, period, customFrom, customTo, onSaved }: Props) {
  const [name, setName] = useState(`${accountName} quick report - copy`);
  const [shareWith, setShareWith] = useState("only-me");
  const [groupSelection, setGroupSelection] = useState<string>("");
  const [newGroupName, setNewGroupName] = useState("");
  const [existingGroups, setExistingGroups] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setName(`${accountName} quick report - copy`);
    setShareWith("only-me");
    setGroupSelection("");
    setNewGroupName("");
    try {
      const raw = window.localStorage.getItem(GROUPS_KEY);
      setExistingGroups(raw ? JSON.parse(raw) : []);
    } catch {}
  }, [open, accountName]);

  if (!open) return null;

  const isAddingNew = groupSelection === ADD_NEW;

  const shareOptions = [
    { label: "Only me", value: "only-me" },
    { label: "Everyone in the business", value: "everyone" },
    { label: "Everyone with access to Reports", value: "reports-access" },
  ];

  const groupOptions = [
    ...existingGroups.map((g) => ({ label: g, value: g })),
    { label: "+ Add new group", value: ADD_NEW, divider: true, accent: true },
  ];

  const canSave = name.trim().length > 0 && (!isAddingNew || newGroupName.trim().length > 0);

  const shareHint = shareWith === "only-me"
    ? "This report is only visible to you."
    : shareWith === "everyone"
    ? "Everyone in your business can view and run this report."
    : "Everyone with access to Reports can view and run this report.";

  const handleSave = () => {
    if (!canSave) return;
    let groupName = "";
    if (groupSelection === ADD_NEW) {
      groupName = newGroupName.trim();
      if (groupName && !existingGroups.includes(groupName)) {
        const updated = [...existingGroups, groupName];
        try { window.localStorage.setItem(GROUPS_KEY, JSON.stringify(updated)); } catch {}
      }
    } else if (groupSelection) {
      groupName = groupSelection;
    }

    const report = {
      id: `cr-${Date.now()}`,
      name: name.trim() || `${accountName} quick report - copy`,
      accountId,
      accountName,
      period,
      customFrom,
      customTo,
      savedAt: new Date().toISOString(),
      shareWith,
      group: groupName,
    } as SavedCustomReport & { shareWith: string; group: string };
    try {
      const existing = window.localStorage.getItem(CUSTOM_REPORTS_STORAGE_KEY);
      const list = existing ? JSON.parse(existing) : [];
      list.push(report);
      window.localStorage.setItem(CUSTOM_REPORTS_STORAGE_KEY, JSON.stringify(list));
    } catch {}
    onSaved?.(report);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[9000] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#EEF1F4]">
          <h2 className="text-base font-bold text-ink">Save as new report?</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-ink3 hover:text-ink"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4">
          <p className="text-[12.5px] text-ink3 mb-4">Changes will be saved to the new report. This report will remain unchanged.</p>
          <div className="mb-3.5">
            <label className="block text-[13px] text-ink mb-2 font-medium">Custom report name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="w-full h-[42px] border border-[#D8DEE6] rounded-lg px-3.5 text-[13.5px] bg-white focus:outline-none focus:border-teal-dark focus:ring-2 focus:ring-teal-dark/10" />
            <p className="text-[11.5px] text-ink3 mt-1.5">The name will show in your Custom reports list.</p>
          </div>
          <div className="mb-3.5">
            <label className="block text-[13px] text-ink mb-2 font-medium">Share with</label>
            <CustomDropdown value={shareWith} onChange={setShareWith} options={shareOptions} />
            <p className="text-[11.5px] text-ink3 mt-1.5">{shareHint}</p>
          </div>
          <div className="mb-3.5">
            <label className="block text-[13px] text-ink mb-2 font-medium">Add to group</label>
            <CustomDropdown
              value={groupSelection}
              onChange={setGroupSelection}
              options={groupOptions}
              placeholder="Select"
              searchable={existingGroups.length > 6}
              searchPlaceholder="Search groups"
            />
          </div>
          {isAddingNew && (
            <div className="mb-1">
              <label className="block text-[13px] text-ink mb-2 font-medium">New group name</label>
              <input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder="Enter a name for your report group" className="w-full h-[42px] border border-[#D8DEE6] rounded-lg px-3.5 text-[13.5px] bg-white focus:outline-none focus:border-teal-dark focus:ring-2 focus:ring-teal-dark/10" />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2.5 px-5 py-3 border-t border-[#EEF1F4] bg-[#F7F9FB]">
          <button type="button" onClick={onClose} className="h-[40px] px-5 border border-[#D8DEE6] rounded-[10px] text-[13.5px] font-semibold text-ink bg-white">Cancel</button>
          <button type="button" onClick={handleSave} disabled={!canSave} className="h-[40px] px-5 border border-ink rounded-[10px] text-[13.5px] font-semibold text-white bg-ink disabled:opacity-40 disabled:cursor-not-allowed">Save</button>
        </div>
      </div>
    </div>,
    document.body
  );
}